import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CollectionApiClient, IPaginationParameters, IPaginationResponse, ILookup } from '../../../common';
import { CustomerManagementCollections } from '../../common';
import {
  ICreateCustomerProfileRequest,
  ICreateCustomerProfileResponse,
  ICustomerProfileResponse,
  IPublicOffStreetReservationResponse,
  IReservationListResponse,
  IReservationResponse,
  IReservationsPaginationParameters,
  ISettleOffStreetReservationRequest,
  IUpdateCustomerProfileRequest,
  ICustomerInvoicesPaginationParameters,
  ICustomerInvoiceListResponse
} from '../dtos';
import { ICustomerProfilesApiClient } from './i-customer-profiles-api.client';
import { ICreateVehicleRequest, IVehicleResponse, IVehiclesBasePaginationParameters } from '../../../vehicle-management/vehicles';
import { IOrderResponse, IOnStreetReservationResponse, IOnStreetReservationPaginationParameters, IOrderPaymentRequestResponse, IMyOnStreetReservationDetailsResponse, IPublicOrderPaymentRequestResponse } from '../../../order-management';
import { ICustomerInvoiceResponse, IInvoiceResponse } from '../../../financial-management/invoices';
import {
  ICustomerViolationPaginationParameters,
  IPublicViolationResponse,
  IViolationListResponse
} from '../../../violation-management/violations/dtos';

@Injectable({
  providedIn: 'root'
})
export class CustomerProfilesApiClient implements ICustomerProfilesApiClient {
  public collectionApiClient: CollectionApiClient;

  constructor(protected http: HttpClient) {
    this.collectionApiClient = CollectionApiClient.create(CustomerManagementCollections.CustomerProfiles, http);
  }

  getCollectionApiClient(): CollectionApiClient {
    return this.collectionApiClient;
  }

  create(reqeust: ICreateCustomerProfileRequest): Observable<ICreateCustomerProfileResponse> {
    return this.collectionApiClient.post({
      body: reqeust
    });
  }

  update(id: string, request: IUpdateCustomerProfileRequest): Observable<void> {
    return this.collectionApiClient.put({
      collectionName: CustomerManagementCollections.customerProfile(id),
      body: request
    });
  }

  get(paginationParameters?: IPaginationParameters): Observable<IPaginationResponse<ICustomerProfileResponse>> {
    return this.collectionApiClient.get({ paginationParameters });
  }

  getById(id: string): Observable<ICustomerProfileResponse> {
    return this.collectionApiClient.get({ id });
  }

  getByPhoneNumber(phoneNumber: string): Observable<ICustomerProfileResponse> {
    return this.collectionApiClient.get({ id: phoneNumber });
  }

  getEligibleAccounts(id: string): Observable<IPaginationResponse<ILookup<string>>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.customerProfileEligibleAccounts(id)
    });
  }

  getVehicles(
    id: string,
    paginationParameters?: IVehiclesBasePaginationParameters
  ): Observable<IPaginationResponse<IVehicleResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.customerProfileVehicles(id),
      paginationParameters
    });
  }

  getMyVehicles(paginationParameters?: IVehiclesBasePaginationParameters): Observable<IPaginationResponse<IVehicleResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.customerProfileVehicles('my'),
      paginationParameters
    });
  }

  createVehicle(id: string, request: ICreateVehicleRequest): Observable<IVehicleResponse> {
    return this.collectionApiClient.post({
      collectionName: CustomerManagementCollections.customerProfileVehicles(id),
      body: request
    });
  }

  createMyVehicle(request: ICreateVehicleRequest): Observable<IVehicleResponse> {
    return this.collectionApiClient.post({
      collectionName: CustomerManagementCollections.customerProfileVehicles('my'),
      body: request
    });
  }
  getMyOrders(paginationParameters?: IPaginationParameters): Observable<IPaginationResponse<IOrderResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.customerProfileOrders('my'),
      paginationParameters
    });
  }

  getMyOnStreetReservations(paginationParameters?: IOnStreetReservationPaginationParameters): Observable<IPaginationResponse<IOnStreetReservationResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myOnStreetReservations,
      paginationParameters,
    });
  }

  getMyOnStreetReservationById(id: string): Observable<IMyOnStreetReservationDetailsResponse> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myOnStreetReservation(id),
    });
  }

  getMyViolations(paginationParameters?: ICustomerViolationPaginationParameters): Observable<IPaginationResponse<IViolationListResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myViolations,
      paginationParameters,
    });
  }

  getMyViolationById(id: string): Observable<IPublicViolationResponse> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myViolation(id),
    });
  }

  getMyInvoiceByOrderId(orderId: string): Observable<ICustomerInvoiceResponse> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myInvoiceByOrder(orderId),
    });
  }

  getMyInvoices(paginationParameters?: ICustomerInvoicesPaginationParameters): Observable<IPaginationResponse<ICustomerInvoiceListResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myInvoices,
      paginationParameters
    });
  }

  getMyOffStreetReservations(paginationParameters?: IReservationsPaginationParameters): Observable<IPaginationResponse<IReservationListResponse>> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myOffStreetReservations,
      paginationParameters 
    });
  }

  getMyOffStreetReservationById(id: string): Observable<IPublicOffStreetReservationResponse> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myOffStreetReservation(id) 
    });
  }

  getMyOffStreetReservationByCode(code: string): Observable<IPublicOffStreetReservationResponse> {
    return this.collectionApiClient.get({
      collectionName: CustomerManagementCollections.myOffStreetReservation(code) 
    });
  }

  pickOffStreetReservation(code: string): Observable<void> {
    return this.collectionApiClient.put({
      collectionName: CustomerManagementCollections.pickOffStreetReservation(code) 
    });
  }

  customerSettleOffStreetReservation(code: string, request: ISettleOffStreetReservationRequest): Observable<IPublicOrderPaymentRequestResponse> {
    return this.collectionApiClient.put({
      collectionName: CustomerManagementCollections.customerSettleOffStreetReservation(code),
      body: request
    });
  }

  anonymousSettleOffStreetReservation(code: string, request: ISettleOffStreetReservationRequest): Observable<IPublicOrderPaymentRequestResponse> {
    return this.collectionApiClient.put({
      collectionName: CustomerManagementCollections.anonymousSettleOffStreetReservation(code),
      body: request
    });
  }
}
